import { ColumnDef } from "@tanstack/react-table";
import { Bill, BillAction } from "@/types/bill";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, AlertTriangle, FileText, ArrowUp, ArrowDown } from "lucide-react";
import { BillActionDropdown } from "./BillActionDropdown";
import { BillImpactCell } from "./BillImpactCell";
import { PrintAIReviewButton } from "./PrintAIReviewButton";
import { SortField, SortDirection } from '@/hooks/bills/useBillsWorkflow';

function formatToEastern(dateString?: string) {
  // Example fallback
  return dateString ? new Date(dateString).toLocaleString("en-US", { timeZone: "America/New_York" }) : "";
}


interface BillsColumnsProps {
  processingActions?: Record<string, BillAction>;
  selectedBills: string[];
  selectedSession: string;
  onSelectBill: (billId: string) => void;
  onSelectAll: (bills: Bill[]) => void;
  onMonitorBill: (billId: string) => void;
  onIgnoreBill: (billId: string) => void;
  onProcessAction: (billId: string, action: BillAction) => void;
  onShowImpactDetails?: (bill: Bill) => void;
  handlePDFClick: (url: string, e: React.MouseEvent) => void;
  currentTab: string;
  sortField?: SortField;
  sortDirection?: SortDirection;
  onSortChange?: (field: SortField, direction: SortDirection) => void;
}

export const getBillsColumns = (props: BillsColumnsProps): ColumnDef<Bill>[] => {
  const {
    selectedBills,
    onSelectBill,
    onSelectAll,
    onMonitorBill,
    onIgnoreBill,
    onProcessAction,
    onShowImpactDetails,
    handlePDFClick,
    processingActions,
    sortField,
    sortDirection,
    onSortChange,
    currentTab,
  } = props;


  return [
    {
      id: 'select',
      header: ({ table }) => (
          <div className="flex items-center">
      <span className="sr-only">Select</span>
        <Checkbox
          checked={selectedBills.length === table.getFilteredSelectedRowModel().rows.length && selectedBills.length > 0}
          onCheckedChange={() => onSelectAll(table.getFilteredRowModel().rows.map(row => row.original))}
          aria-label="Select all"
        />
            </div>
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedBills.includes(row.original.billNumber)}
          onCheckedChange={() => onSelectBill(row.original.billNumber)}
          aria-label={`Select bill ${row.original.billNumber}`}
        />
      ),
      enableSorting: false,
      size: 12,
    },
    {
      accessorKey: 'billNumber',
      header: () => {
        return (
          <Button
            variant="ghost"
            className="flex items-center gap-1 px-1 hover:bg-gray-100 rounded"
            onClick={() => onSortChange?.('billNumber', sortField === 'billNumber' && sortDirection === 'desc' ? 'asc' : 'desc')}
          >
            <span>Bill Number</span>
            {sortField === 'billNumber' && (
              sortDirection === 'asc' ? <ArrowUp className="h-4 w-4 ml-1" /> : <ArrowDown className="h-4 w-4 ml-1" />
            )}
          </Button>
        );
      },
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {row.original.billNumber}
          {!row.original.fullTextAvailable && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                </TooltipTrigger>
                <TooltipContent>
                  Full text not available
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {row.original.url && row.original.fullTextAvailable && (
            <>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => handlePDFClick(row.original.url, e)}
                      className="px-2 w-8 h-8"
                    >
                      <FileText className="h-4 w-4" />
                      <span className="sr-only">View Bill PDF</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    View Bill PDF
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {row.original.versions && row.original.versions.length > 0 && (
                <>
                  <PrintAIReviewButton
                    latestVersion={row.original.versions[row.original.versions.length - 1]}
                    bill={row.original}
                    loadingAction={processingActions?.[row.original.billNumber]}
                  />
                  {/*<PromptResultsDropdown*/}
                  {/*  latestVersion={row.original.versions[row.original.versions.length - 1]}*/}
                  {/*  loadingAction={processingActions?.[row.original.billNumber]}*/}
                  {/*/>*/}
                </>
              )}
            </>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'title',
      header: () => {
        return (
          <Button
            variant="ghost"
            className="flex items-center gap-1 px-1 hover:bg-gray-100 rounded"
            onClick={() => onSortChange?.('title', sortField === 'title' && sortDirection === 'desc' ? 'asc' : 'desc')}
          >
            <span>Title</span>
            {sortField === 'title' && (
              sortDirection === 'asc' ? <ArrowUp className="h-4 w-4 ml-1" /> : <ArrowDown className="h-4 w-4 ml-1" />
            )}
          </Button>
        );
      },
      enableSorting: false,
      cell: ({ row }) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <div className="truncate max-w-md">
                {row.original.title || 'No Title'}
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-lg">
              {row.original.synopsis || 'No synopsis available'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
    {
      id: 'impact',
      header: () => {
        return (
          <Button
            variant="ghost"
            className="flex items-center gap-1 px-1 hover:bg-gray-100 rounded"
            onClick={() => onSortChange?.('impact', sortField === 'impact' && sortDirection === 'desc' ? 'asc' : 'desc')}
          >
            <span>Impact</span>
            {sortField === 'impact' && (
              sortDirection === 'asc' ? <ArrowUp className="h-4 w-4 ml-1" /> : <ArrowDown className="h-4 w-4 ml-1" />
            )}
          </Button>
        );
      },
      enableSorting: false,
      // Create a custom accessor function that generates a sortable value for impact
      accessorFn: (bill: Bill) => {
        const impact = bill.monitor_analysis?.summary?.overall_impact;
        if (!impact) return { type: 'no-analysis', value: -999 }; // No analysis should sort to the end

        // Create a numeric value for sorting that prioritizes type first, then score
        // This creates three tiers (positive, neutral, negative) with scores affecting order within each tier
        const typeValue = { positive: 1000, neutral: 0, negative: -1000 };
        return {
          type: impact.type,
          score: impact.score,
          value: typeValue[impact.type] + impact.score
        };
      },
      cell: ({ row }) => (
        <BillImpactCell
          analysis={row.original.monitor_analysis}
          onClick={() => onShowImpactDetails?.(row.original)}
        />
      ),
    },
    {
      accessorKey: 'crossfileBillNumber',
      header: () => {
        return (
          <Button
            variant="ghost"
            className="flex items-center gap-1 px-1 hover:bg-gray-100 rounded"
            onClick={() => onSortChange?.('crossfileBillNumber', sortField === 'crossfileBillNumber' && sortDirection === 'desc' ? 'asc' : 'desc')}
          >
            <span>Crossfile</span>
            {sortField === 'crossfileBillNumber' && (
              sortDirection === 'asc' ? <ArrowUp className="h-4 w-4 ml-1" /> : <ArrowDown className="h-4 w-4 ml-1" />
            )}
          </Button>
        );
      },
      enableSorting: false,
      cell: ({ row }) => (
        row.original.crossfileBillNumber ? (
          <Badge variant="outline">
            {row.original.crossfileBillNumber}
          </Badge>
        ) : (
          "-"
        )
      ),
    },
    {
      accessorKey: 'lastModified',
      header: () => {
        return (
          <Button
            variant="ghost"
            className="flex items-center gap-1 px-1 hover:bg-gray-100 rounded"
            onClick={() => onSortChange?.('lastModified', sortField === 'lastModified' && sortDirection === 'desc' ? 'asc' : 'desc')}
          >
            <span>Modified</span>
            {sortField === 'lastModified' && (
              sortDirection === 'asc' ? <ArrowUp className="h-4 w-4 ml-1" /> : <ArrowDown className="h-4 w-4 ml-1" />
            )}
          </Button>
        );
      },
      enableSorting: false,
      cell: ({ row }) => formatToEastern(row.original.lastModified),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const bill = row.original;
        return (
          <div className="flex justify-end gap-2">
            {currentTab === "New" && (
              <>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onMonitorBill(bill.billNumber)}
                        className="px-2 w-8 h-8"
                      >
                        <Eye className="h-4 w-4" />
                        <span className="sr-only">Monitor Bill</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Monitor Bill
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onIgnoreBill(bill.billNumber)}
                        className="px-2 w-8 h-8"
                      >
                        <EyeOff className="h-4 w-4" />
                        <span className="sr-only">Ignore Bill</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Ignore Bill
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
            {currentTab === "Monitoring" && (
              <>
                <BillActionDropdown
                  onAction={(action: BillAction) => onProcessAction(bill.billNumber, action)}
                  disabled={!bill.fullTextAvailable || !!processingActions?.[bill.billNumber]}
                />
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onIgnoreBill(bill.billNumber)}
                        className="px-2 w-8 h-8"
                      >
                        <EyeOff className="h-4 w-4" />
                        <span className="sr-only">Stop Monitoring</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Stop Monitoring
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
            {currentTab === "Ignored" && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onMonitorBill(bill.billNumber)}
                      className="px-2 w-8 h-8"
                    >
                      <Eye className="h-4 w-4" />
                      <span className="sr-only">Resume Monitoring</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Resume Monitoring
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      },
      enableSorting: false,
      size: 150,
    },
  ];
};